import { TokenManager, authenticatedFetch } from "@/supabase/supabaseClient";
import type { ArcgisLayerConfig, LayerFormData } from "@/types/layers";
import { TASK_LAYER_KEY } from "@/constant/layers";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

export class LayerService {
  /**
   * Fetch all ArcGIS layers
   */
  static async getAllLayers(tenantId: string): Promise<ArcgisLayerConfig[]> {
    try {
      // Ensure token is ready before making API call
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/asset_layers?select=*&or=(tenant_id.eq.${tenantId},tenant_id.is.null)&order=display_order.asc.nullslast,created_at.asc`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching arcgis layers:", error);
        throw new Error(`Failed to fetch layers: ${response.statusText}`);
      }

      const data = await response.json();
      // Debug log removed after verification of layer ordering

      return data || [];
    } catch (error) {
      console.error("Error in getAllLayers:", error);
      throw error;
    }
  }

  /**
   * Get layer by key
   */
  static async getLayerByKey(
    key: string,
    tenantId: string
  ): Promise<ArcgisLayerConfig | null> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch layer by key");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/asset_layers?select=*&key=eq.${key}&or=(tenant_id.eq.${tenantId},tenant_id.is.null)`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching layer by key:", error);
        return null;
      }

      const data = await response.json();
      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error("Error in getLayerByKey:", error);
      return null;
    }
  }

  /**
   * Create a new ArcGIS layer
   */
  static async createLayer(
    layerData: LayerFormData,
    tenantId: string
  ): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot create layer");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/asset_layers`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Prefer: "return=minimal",
          },
          body: JSON.stringify({ ...layerData, tenant_id: tenantId }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error creating arcgis layer:", error);
        throw new Error(`Failed to create layer: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error in createLayer:", error);
      throw error;
    }
  }

  /**
   * Update an existing ArcGIS layer
   */
  static async updateLayer(
    key: string,
    layerData: Partial<LayerFormData>,
    tenantId: string
  ): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot update layer");
        throw new Error("Authentication token not available");
      }

      const updateData = {
        ...layerData,
        updated_at: new Date().toISOString(),
      };

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/asset_layers?key=eq.${key}&tenant_id=eq.${tenantId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Prefer: "return=minimal",
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error updating arcgis layer:", error);
        throw new Error(`Failed to update layer: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error in updateLayer:", error);
      throw error;
    }
  }

  /**
   * Delete an ArcGIS layer
   */
  static async deleteLayer(key: string, tenantId: string): Promise<void> {
    try {
      // Prevent deletion of reserved Task Layer
      if (key === TASK_LAYER_KEY) {
        throw new Error(
          "The Task Layer is a reserved system layer and cannot be deleted."
        );
      }

      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot delete layer");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/asset_layers?key=eq.${key}&tenant_id=eq.${tenantId}`,
        {
          method: "DELETE",
          headers: {
            Prefer: "return=minimal",
          },
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error deleting arcgis layer:", error);
        throw new Error(`Failed to delete layer: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error in deleteLayer:", error);
      throw error;
    }
  }

  /**
   * Update the display order of layers
   */
  static async updateLayersOrder(
    layerOrders: { key: string; display_order: number }[],
    tenantId: string
  ): Promise<void> {
    try {
      // Debug log removed

      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      // Update each layer's display_order
      const updatePromises = layerOrders.map(async ({ key, display_order }) => {
        const response = await authenticatedFetch(
          `${supabaseUrl}/rest/v1/asset_layers?key=eq.${key}&tenant_id=eq.${tenantId}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ display_order }),
          }
        );

        if (!response.ok) {
          const error = await response.text();
          console.error(`Error updating layer order for ${key}:`, error);
          throw new Error(
            `Failed to update layer order: ${response.statusText}`
          );
        }
      });

      await Promise.all(updatePromises);
      // Debug log removed
    } catch (error) {
      console.error("Error in updateLayersOrder:", error);
      throw error;
    }
  }
}

// Export the functions for backward compatibility
export const fetchArcgisLayers = LayerService.getAllLayers;
export const createArcgisLayer = LayerService.createLayer;
export const updateArcgisLayer = LayerService.updateLayer;
export const deleteArcgisLayer = LayerService.deleteLayer;
